import { Box, Center, Heading, HStack, SimpleGrid } from '@chakra-ui/react'
import { collection, deleteDoc, doc, getDoc, getDocs, setDoc } from 'firebase/firestore'
import { createContext, useCallback, useEffect, useState } from 'react'
import { useLoaderData } from 'react-router-dom'
import Score from '../components/Score'
import { db } from '../firebaseHandler'

import ScoreResults from '../components/ScoreResults'
import ScoreListingHeader from '../components/ScoreListingHeader'
import SubmitWeekUrl from '../components/SubmitWeekUrl'
import Message from '../components/Message'
import WeekSettingsModal from '../components/WeekSettingsModal'
import ResetWeekModal from '../components/ResetWeekModal'
import { ScoreWithRef, SongDocument, Status, WeekDocument } from '../types'

// export const ENV = 'prod'
export const ENV = process.env.NODE_ENV === 'development' ? 'dev' : 'prod'

enum DataStatusEnum {
  FETCHING = 'FETCHING',
  NO_WEEK = 'NO_WEEK',
  NO_SONGS = 'NO_SONGS',
  SCORES_FETCHED = 'SCORES_FETCHED',
}

export const levelsSorted = [
  'Beginner',
  'Beginner Bonus',
  'Intermediate',
  'Intermediate Bonus',
  'Advanced',
  'Advanced Bonus',
  'Masterclass',
  'Masterclass Bonus',
  'God of Guitar',
  'God of Rhythm',
  'God of Bass',
  'Tribute',
]

// Define the type for the context
interface ScoresContextType {
  weekNum: string
  weekDocument: WeekDocument | null
  scores: ScoreWithRef[]
  setScores: React.Dispatch<React.SetStateAction<ScoreWithRef[]>>
  filteredScores: ScoreWithRef[]
  setFilteredScores: React.Dispatch<React.SetStateAction<ScoreWithRef[]>>
  songs: SongDocument[]
  setSongs: React.Dispatch<React.SetStateAction<SongDocument[]>>
  pages: number[]
  setPages: React.Dispatch<React.SetStateAction<number[]>>
  applyFilters: () => void
  changeSelectedPages: (selected: any) => void
  changeSelectedStatuses: (selected: any, selectCallback: any) => void
  dataStatus: DataStatusEnum
  setDataStatus: React.Dispatch<React.SetStateAction<DataStatusEnum>>
  weekUrlSubmitted: boolean
  setWeekUrlSubmitted: React.Dispatch<React.SetStateAction<boolean>>
  statusOptions: StatusOption[]
  filters: FiltersType
  fetchScores: () => Promise<void>
  createNewFirestoreWeek: (weekUrl: string, weekNum: number) => Promise<void>
  updateWeekDocument: (updates: { url: string; weekNum: number }) => Promise<void>
  resetWeekData: () => Promise<void>
  approvedScores: ScoreWithRef[]
  pushedScores: ScoreWithRef[]
}

export const ScoresContext = createContext<ScoresContextType | undefined>(undefined)

export async function loader({ params }: { params: { weekNum: string } }): Promise<string> {
  return params.weekNum
}

// Define the type for status options
interface StatusOption {
  label: string
  value: string
  type: string
}

// Define the type for filters
interface FiltersType {
  pages: { label: number; value: number }[]
  statuses: StatusOption[]
}

//TODO: take statuses from score machine?
const statuses: StatusOption[] = [
  { label: 'SCRAPED', value: 'SCRAPED', type: 'approved_pushed' },
  { label: 'NOT_RECOGNIZED', value: 'NOT_RECOGNIZED', type: 'other' },
  // { label: 'EDITING', value: 'EDITING' },
  { label: 'AUTO_APPROVED', value: 'AUTO_APPROVED', type: 'approved_pushed' },
  { label: 'MANUALLY_APPROVED', value: 'MANUALLY_APPROVED', type: 'approved_pushed' },
  { label: 'DOWNLOAD_FAILED', value: 'DOWNLOAD_FAILED', type: 'other' },
  { label: 'ERROR', value: 'ERROR', type: 'other' },
  { label: 'INVALID_SCORE', value: 'INVALID_SCORE', type: 'other' },
  { label: 'PUSHED', value: 'PUSHED', type: 'approved_pushed' },
  { label: 'CLASSIFICATION_FAILED', value: 'CLASSIFICATION_FAILED', type: 'other' },
]

interface SortOptions<T> {
  data: T[]
  sortBy: string[]
  sortField: keyof T
}

const customSort = <T extends Record<string, any>>({
  data,
  sortBy,
  sortField,
}: SortOptions<T>): T[] => {
  const sortByObject = sortBy.reduce(
    (obj, item, index) => {
      return {
        ...obj,
        [item]: index,
      }
    },
    {} as Record<string, number>
  )

  return [...data].sort((a, b) => {
    const aValue = a[sortField]
    const bValue = b[sortField]
    return sortByObject[aValue] - sortByObject[bValue]
  })
}

export default function ScoreListing() {
  const weekNum = useLoaderData() as string

  const [dataStatus, setDataStatus] = useState<DataStatusEnum>(DataStatusEnum.FETCHING)
  const [weekUrlSubmitted, setWeekUrlSubmitted] = useState<boolean>(false)
  const [weekDocument, setWeekDocument] = useState<WeekDocument | null>(null)
  const [pages, setPages] = useState<number[]>([])
  const [scores, setScores] = useState<ScoreWithRef[]>([])
  const [filteredScores, setFilteredScores] = useState<ScoreWithRef[]>([])
  const [songs, setSongs] = useState<SongDocument[]>([])
  const [filters, setFilters] = useState<FiltersType>({
    pages: pages.map((x) => ({ label: x, value: x })),
    statuses: statuses,
  })

  const fetchScores = useCallback(async (): Promise<void> => {
    // TODO: promise all to do a concurrent
    const docRef = doc(db, `/scores_${ENV}/week${weekNum}`)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const weekData = docSnap.data() as WeekDocument
      setWeekDocument(weekData)

      if (
        'championshipSongs' in weekData &&
        Array.isArray(weekData.championshipSongs) &&
        weekData.championshipSongs.length > 0
      ) {
        const songsSorted = customSort<SongDocument>({
          data: weekData.championshipSongs,
          sortBy: levelsSorted,
          sortField: 'level',
        })

        setSongs(songsSorted)

        const querySnapshot = await getDocs(collection(db, `/scores_${ENV}/week${weekNum}/scores`))
        const scoresData = querySnapshot.docs.map(
          (doc) => Object.assign(doc.data(), { ref: doc.ref }) as ScoreWithRef
        )

        setScores(scoresData)

        // Fix: Use Array.from instead of spread operator with Set
        const uniquePages = Array.from(new Set(scoresData.map((x) => x.page)))
        setPages(uniquePages)
        setFilters((prev) => ({
          ...prev,
          pages: uniquePages.map((x) => ({ label: x, value: x })),
        }))
        setDataStatus(DataStatusEnum.SCORES_FETCHED)
      } else {
        setDataStatus(DataStatusEnum.NO_SONGS)
      }
    } else {
      // doc.data() will be undefined in this case
      setDataStatus(DataStatusEnum.NO_WEEK)
      setWeekDocument(null)
      console.log('No such document!')
      // throw Error('No week doc in db')
    }
  }, [weekNum])

  const createNewFirestoreWeek = async (weekUrl: string, weekNum: number): Promise<void> => {
    const docRef = doc(db, `/scores_${ENV}/week${weekNum}`)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      throw new Error('Week already exists!')
    } else {
      await setDoc(doc(db, `scores_${ENV}`, `week${weekNum}`), {
        url: weekUrl,
        weekNum: weekNum,
      })
    }
  }

  const updateWeekDocument = async ({
    url: updatedUrl,
    weekNum: updatedWeekNum,
  }: {
    url: string
    weekNum: number
  }): Promise<void> => {
    const docRef = doc(db, `/scores_${ENV}/week${weekNum}`)
    await setDoc(
      docRef,
      {
        url: updatedUrl,
        weekNum: updatedWeekNum,
      },
      { merge: true }
    )

    setWeekDocument((prev) =>
      prev
        ? {
            ...prev,
            url: updatedUrl,
            weekNum: updatedWeekNum,
          }
        : {
            url: updatedUrl,
            weekNum: updatedWeekNum,
            championshipSongs: [],
            lastScraped: '',
            numPages: 0,
          }
    )
  }

  const resetWeekData = async (): Promise<void> => {
    const docRef = doc(db, `/scores_${ENV}/week${weekNum}`)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      throw new Error('Week document not found.')
    }

    const weekData = docSnap.data() as WeekDocument
    const storedUrl = weekData.url
    const storedWeekNum =
      typeof weekData.weekNum === 'number' && !Number.isNaN(weekData.weekNum)
        ? weekData.weekNum
        : Number.parseInt(String(weekData.weekNum ?? weekNum), 10)

    if (!storedUrl || Number.isNaN(storedWeekNum)) {
      throw new Error('Week document is missing url or week number.')
    }

    const scoresCollectionRef = collection(db, `/scores_${ENV}/week${weekNum}/scores`)
    const scoresSnapshot = await getDocs(scoresCollectionRef)
    const deleteOperations = scoresSnapshot.docs.map((scoreDoc) => deleteDoc(scoreDoc.ref))
    if (deleteOperations.length > 0) {
      await Promise.all(deleteOperations)
    }

    await deleteDoc(docRef)

    await setDoc(docRef, {
      url: storedUrl,
      weekNum: storedWeekNum,
    })

    setScores([])
    setFilteredScores([])
    setSongs([])
    setPages([])
    setFilters((prev) => ({
      ...prev,
      pages: [],
    }))
    setWeekDocument({
      url: storedUrl,
      weekNum: storedWeekNum,
      championshipSongs: [],
      lastScraped: '',
      numPages: 0,
    })
    setDataStatus(DataStatusEnum.NO_SONGS)
  }

  const applyFilters = useCallback((): void => {
    const newFilteredScores = getFilteredScores(scores, filters)
    setFilteredScores(newFilteredScores)
  }, [scores, filters])

  useEffect(() => {
    fetchScores()
  }, [fetchScores])

  useEffect(() => {
    if (weekUrlSubmitted) {
      ;(async () => {
        try {
          await fetchScores()
        } finally {
          setWeekUrlSubmitted(false)
        }
      })()
    }
  }, [weekUrlSubmitted, fetchScores])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const getFilteredScores = (scores: ScoreWithRef[], filters: FiltersType): ScoreWithRef[] => {
    return scores.filter((score) => {
      return (
        filters.pages.some((item) => item.value === score.page) &&
        filters.statuses.some((item) => item.value === score.status)
      )
    })
  }

  const changeSelectedStatuses = (selected: any, selectCallback: any): void => {
    // no selected -> all selected
    if (selected.length === 0) {
      setFilters({ ...filters, statuses: statuses })
    } else {
      setFilters({ ...filters, statuses: Array.isArray(selected) ? selected : [selected.value] })
    }
    selectCallback(selected)
  }

  const changeSelectedPages = (selected: any): void => {
    if (selected.length === 0) {
      setFilters({ ...filters, pages: pages.map((x) => ({ label: x, value: x })) })
    } else {
      setFilters({
        ...filters,
        pages: Array.isArray(selected) ? selected : [selected.value],
      })
    }
  }

  const approvedScores = scores.filter((score) =>
    [Status.AUTO_APPROVED, Status.MANUALLY_APPROVED].includes(score.status as Status)
  )

  const pushedScores = scores.filter((score) => score.status === Status.PUSHED)

  return (
    <ScoresContext.Provider
      value={{
        weekNum,
        scores,
        setScores,
        weekDocument,
        filteredScores,
        setFilteredScores,
        songs,
        setSongs,
        pages,
        setPages,
        applyFilters,
        changeSelectedPages,
        changeSelectedStatuses,
        dataStatus,
        setDataStatus,
        weekUrlSubmitted,
        setWeekUrlSubmitted,
        statusOptions: statuses,
        filters,
        fetchScores,
        createNewFirestoreWeek,
        updateWeekDocument,
        resetWeekData,
        approvedScores,
        pushedScores,
      }}
    >
      <Box>
        <Center bg="tomato" h="60px" color="white" px={4}>
          <HStack spacing={3}>
            <Heading as="h1">Rocksmith Champ Score Assistant - Week {weekNum as string}</Heading>
            <WeekSettingsModal />
            <ResetWeekModal />
          </HStack>
        </Center>

        {dataStatus === DataStatusEnum.FETCHING && (
          <Message message="Loading data..." addSpinner={true} />
        )}
        {dataStatus === DataStatusEnum.NO_WEEK && <SubmitWeekUrl />}
        {dataStatus === DataStatusEnum.NO_SONGS && (
          <Message message="Week is present, but songs aren't fetched yet. Please return after some time." />
        )}
        {dataStatus === DataStatusEnum.SCORES_FETCHED && (
          <Box p={10}>
            <ScoreListingHeader />

            <SimpleGrid columns={[1, 1, 1, 1, 2, 3]} spacing="40px" mt={10}>
              {filteredScores.map((score) => {
                return <Score key={score.uniqueId} score={score} />
              })}
            </SimpleGrid>

            <ScoreResults />
          </Box>
        )}
      </Box>
    </ScoresContext.Provider>
  )
}
